// ============================================================================
// Raisonnement géométrique (moteur v4) : la feuille de brouillon des preuves.
// ============================================================================
// Une ligne est UNE proposition, écrite comme au collège :
//   « (d₁) // (d₂) »   « (AB) ⊥ (BC) »   « I milieu de [AB] »   « J ∈ [AC] »
//   « ABCD parallélogramme »   « ABC rectangle en A »
// Chaque proposition doit découler, en UNE propriété du cours, de l'énoncé et
// des lignes déjà écrites :
//   - deux droites perpendiculaires à une même droite sont parallèles ;
//   - deux droites parallèles à une même droite sont parallèles ;
//   - si deux droites sont parallèles, toute perpendiculaire à l'une est
//     perpendiculaire à l'autre ;
//   - théorème des milieux, et sa réciproque ;
//   - parallélogramme ⇔ côtés opposés parallèles ⇔ diagonales de même milieu ;
//   - triangle rectangle ⇔ deux côtés perpendiculaires.
// Le but : la proposition demandée (`geometry.goal`). Le corrigé (`path`) est
// rejoué par le juge à la validation. Rien n'est « vrai » sans propriété : une
// proposition qui ne découle de rien est refusée (« D'où vient-elle ? »).
// Ce mode est séparé du moteur des égalités : il ne change rien aux autres feuilles.
// ============================================================================

import type { Chip } from './lexer';
import type { Line, Node } from './tree';

export interface GeometrySpec {
  goal: string;
  path: string[];
}

export type Prop =
  | { k: 'par'; a: string; b: string }
  | { k: 'perp'; a: string; b: string }
  | { k: 'mid'; p: string; s: string }
  | { k: 'on'; p: string; s: string }
  | { k: 'para'; v: string }
  | { k: 'rect'; t: string; at: string };

export class GeometrySyntaxError extends Error {}

const PT = "[A-Z][′']?";
const RE_LINE = new RegExp(`^\\((?:(${PT})(${PT})|([a-zΔ][₀-₉]*))\\)$`);
const RE_SEG = new RegExp(`^\\[(${PT})(${PT})\\]$`);
const RE_PT = new RegExp(`^${PT}$`);

function pts(s: string): string[] {
  return s.match(/[A-Z][′']?/g) ?? [];
}

/** droite canonique : (BA) = (AB) ; (d₁) gardée telle quelle */
function lineKey(t: string): string | null {
  const m = RE_LINE.exec(t);
  if (!m) return null;
  if (m[3]) return `(${m[3]})`;
  const [a, b] = [m[1]!, m[2]!].sort();
  return a === b ? null : `(${a}${b})`;
}
function segKey(t: string): string | null {
  const m = RE_SEG.exec(t);
  if (!m) return null;
  const [a, b] = [m[1]!, m[2]!].sort();
  return a === b ? null : `[${a}${b}]`;
}

/** lit une proposition (texte de la ligne, bouts joints par des espaces) */
export function parseProp(text: string): Prop {
  const s = text
    .replace(/\u2009|\u202F|\u00A0/g, ' ')
    .replace(/∥/g, '//')
    .replace(/\s+/g, ' ')
    .trim();
  let m = /^(\S+) (\/\/|⊥) (\S+)$/.exec(s);
  if (m) {
    const a = lineKey(m[1]!);
    const b = lineKey(m[3]!);
    if (!a || !b)
      throw new GeometrySyntaxError('Une droite s’écrit entre parenthèses : (AB) ou (d₁).');
    if (a === b) throw new GeometrySyntaxError('Il faut deux droites différentes.');
    const [x, y] = [a, b].sort();
    return { k: m[2] === '//' ? 'par' : 'perp', a: x!, b: y! };
  }
  m = /^(\S+) (?:est (?:le )?)?milieu de (\S+)$/.exec(s);
  if (m) {
    const seg = segKey(m[2]!);
    if (!RE_PT.test(m[1]!))
      throw new GeometrySyntaxError('Le milieu est un point : une lettre majuscule.');
    if (!seg) throw new GeometrySyntaxError('Un segment s’écrit entre crochets : [AB].');
    return { k: 'mid', p: m[1]!, s: seg };
  }
  m = /^(\S+) ∈ (\S+)$/.exec(s);
  if (m) {
    const seg = segKey(m[2]!) ?? lineKey(m[2]!);
    if (!RE_PT.test(m[1]!) || !seg) throw new GeometrySyntaxError('Écris « J ∈ [AC] ».');
    return { k: 'on', p: m[1]!, s: seg };
  }
  m = /^([A-Z][′']?){4} (?:est un )?parallélogramme$/.exec(s);
  if (m) return { k: 'para', v: s.split(' ')[0]! };
  m = /^(\S+) (?:est )?rectangle en (\S+)$/.exec(s);
  if (m && pts(m[1]!).length === 3 && RE_PT.test(m[2]!) && pts(m[1]!).includes(m[2]!))
    return { k: 'rect', t: m[1]!, at: m[2]! };
  throw new GeometrySyntaxError(
    'Écris une seule propriété : « (d₁) // (d₂) », « (AB) ⊥ (BC) », « I milieu de [AB] », « ABCD parallélogramme »…',
  );
}

/** clé d'une proposition (ABCD parallélogramme = BCDA… : on garde le cycle) */
export function propKey(p: Prop): string {
  switch (p.k) {
    case 'par':
      return `${p.a}//${p.b}`;
    case 'perp':
      return `${p.a}⊥${p.b}`;
    case 'mid':
      return `${p.p} mil ${p.s}`;
    case 'on':
      return `${p.p} ∈ ${p.s}`;
    case 'para': {
      const v = pts(p.v);
      const rots: string[] = [];
      for (let i = 0; i < 4; i++) {
        const r = [...v.slice(i), ...v.slice(0, i)];
        rots.push(r.join(''), [...r].reverse().join(''));
      }
      return 'para ' + rots.sort()[0];
    }
    case 'rect': {
      const others = pts(p.t)
        .filter((x) => x !== p.at)
        .sort();
      return `rect ${p.at} ${others.join('')}`;
    }
  }
}
export function propText(p: Prop): string {
  switch (p.k) {
    case 'par':
      return `${p.a} // ${p.b}`;
    case 'perp':
      return `${p.a} ⊥ ${p.b}`;
    case 'mid':
      return `${p.p} milieu de ${p.s}`;
    case 'on':
      return `${p.p} ∈ ${p.s}`;
    case 'para':
      return `${p.v} parallélogramme`;
    case 'rect':
      return `${p.t} rectangle en ${p.at}`;
  }
}

function line2(a: string, b: string): string {
  const [x, y] = [a, b].sort();
  return `(${x}${y})`;
}
function seg2(a: string, b: string): string {
  const [x, y] = [a, b].sort();
  return `[${x}${y}]`;
}
function segPts(s: string): [string, string] {
  const p = pts(s);
  return [p[0]!, p[1]!];
}

export interface GeoState {
  spec: GeometrySpec;
  goal: Prop;
  facts: Map<string, Prop>;
}

export function createGeoState(spec: GeometrySpec, givens: string[] = []): GeoState {
  const gs: GeoState = { spec, goal: parseProp(spec.goal), facts: new Map() };
  givens.forEach((g) => {
    try {
      const p = parseProp(g);
      gs.facts.set(propKey(p), p);
    } catch {
      /* donnée illisible : ignorée */
    }
  });
  return gs;
}

/** ce qu'on sait : propositions établies, plus leurs conséquences « de définition » */
function has(gs: GeoState, p: Prop): boolean {
  return gs.facts.has(propKey(p));
}
const facts = (gs: GeoState, k: Prop['k']) => [...gs.facts.values()].filter((f) => f.k === k);

/**
 * Points alignés connus : les extrémités d'un segment, son milieu, et les points
 * déclarés sur lui (« J ∈ [AC] »). Deux ensembles qui partagent deux points sont
 * sur la même droite : on les fusionne.
 */
function alignments(gs: GeoState): string[][] {
  const sets: string[][] = [];
  for (const f of gs.facts.values()) {
    if (f.k !== 'mid' && f.k !== 'on') continue;
    const ends = pts(f.s);
    if (ends.length === 2) sets.push([...ends, f.p]);
  }
  let merged = true;
  while (merged) {
    merged = false;
    for (let i = 0; i < sets.length && !merged; i++)
      for (let j = i + 1; j < sets.length && !merged; j++) {
        const common = sets[i]!.filter((x) => sets[j]!.includes(x));
        if (common.length >= 2) {
          sets[i] = [...new Set([...sets[i]!, ...sets[j]!])];
          sets.splice(j, 1);
          merged = true;
        }
      }
  }
  return sets;
}
/** droite canonique à alignement près : (BK) = (BC) quand K est sur [BC] */
function canonLine(gs: GeoState, key: string): string {
  const p = pts(key);
  if (p.length !== 2) return key;
  const set = alignments(gs).find((s) => s.includes(p[0]!) && s.includes(p[1]!));
  if (!set) return key;
  const [a, b] = [...set].sort();
  return line2(a!, b!);
}
/** paires (x, y) de droites liées par la relation k (dans les deux sens), à alignement près */
function rel(gs: GeoState, k: 'par' | 'perp'): [string, string][] {
  return facts(gs, k).flatMap((f) => {
    const g = f as { a: string; b: string };
    const a = canonLine(gs, g.a);
    const b = canonLine(gs, g.b);
    return [
      [a, b],
      [b, a],
    ] as [string, string][];
  });
}
function related(gs: GeoState, k: 'par' | 'perp', x: string, y: string): boolean {
  const cx = canonLine(gs, x);
  const cy = canonLine(gs, y);
  return rel(gs, k).some(([a, b]) => a === cx && b === cy);
}
function sameLine(gs: GeoState, x: string, y: string): boolean {
  return canonLine(gs, x) === canonLine(gs, y);
}

/** une propriété du cours qui justifie p en un pas, sinon null */
export function justify(gs: GeoState, p: Prop): string | null {
  if (p.k === 'par') {
    if (related(gs, 'par', p.a, p.b)) return 'Ce sont les mêmes droites, à alignement près';
    const pa = canonLine(gs, p.a);
    const pb = canonLine(gs, p.b);
    for (const [x, z] of rel(gs, 'perp'))
      if (x === pa && related(gs, 'perp', pb, z))
        return 'Deux droites perpendiculaires à une même droite sont parallèles';
    for (const [x, z] of rel(gs, 'par'))
      if (x === pa && z !== pb && related(gs, 'par', pb, z))
        return 'Deux droites parallèles à une même droite sont parallèles';
    // théorème des milieux : I milieu de [AB], J milieu de [AC] ⇒ (IJ) // (BC)
    const mids = facts(gs, 'mid') as { p: string; s: string }[];
    for (const m1 of mids)
      for (const m2 of mids) {
        if (m1 === m2) continue;
        const [a1, b1] = segPts(m1.s);
        const [a2, b2] = segPts(m2.s);
        const common = [a1, b1].find((x) => x === a2 || x === b2);
        if (!common) continue;
        const o1 = common === a1 ? b1 : a1;
        const o2 = common === a2 ? b2 : a2;
        if (o1 === o2) continue;
        const ij = line2(m1.p, m2.p);
        const bc = line2(o1, o2);
        if (
          (sameLine(gs, p.a, ij) && sameLine(gs, p.b, bc)) ||
          (sameLine(gs, p.a, bc) && sameLine(gs, p.b, ij))
        )
          return 'Théorème des milieux';
      }
    // côtés opposés d'un parallélogramme
    for (const f of facts(gs, 'para') as { v: string }[]) {
      const [A, B, C, D] = pts(f.v);
      const pairs = [
        [line2(A!, B!), line2(C!, D!)],
        [line2(A!, D!), line2(B!, C!)],
      ];
      if (
        pairs.some(
          ([x, y]) =>
            (sameLine(gs, p.a, x!) && sameLine(gs, p.b, y!)) ||
            (sameLine(gs, p.a, y!) && sameLine(gs, p.b, x!)),
        )
      )
        return 'Les côtés opposés d’un parallélogramme sont parallèles';
    }
    return null;
  }
  if (p.k === 'perp') {
    for (const [x, z] of rel(gs, 'par'))
      for (const [u, w] of [
        [p.a, p.b],
        [p.b, p.a],
      ] as [string, string][])
        if (x === u && related(gs, 'perp', z, w))
          return 'Si deux droites sont parallèles, toute perpendiculaire à l’une est perpendiculaire à l’autre';
    for (const f of facts(gs, 'rect') as { t: string; at: string }[]) {
      const [x, y] = pts(f.t).filter((q) => q !== f.at);
      const l1 = line2(f.at, x!);
      const l2 = line2(f.at, y!);
      if ((p.a === l1 && p.b === l2) || (p.a === l2 && p.b === l1))
        return 'Dans un triangle rectangle, les côtés de l’angle droit sont perpendiculaires';
    }
    return null;
  }
  if (p.k === 'rect') {
    const [x, y] = pts(p.t).filter((q) => q !== p.at);
    const l1 = line2(p.at, x!);
    const l2 = line2(p.at, y!);
    if (related(gs, 'perp', l1, l2))
      return 'Deux côtés perpendiculaires : le triangle est rectangle';
    return null;
  }
  if (p.k === 'mid') {
    // réciproque du théorème des milieux : I milieu de [AB], J ∈ [AC], (IJ) // (BC) ⇒ J milieu de [AC]
    const [a, c] = segPts(p.s);
    const onOk = has(gs, { k: 'on', p: p.p, s: p.s });
    if (!onOk) return null;
    for (const m of facts(gs, 'mid') as { p: string; s: string }[]) {
      const [m1, m2] = segPts(m.s);
      for (const [A, C] of [
        [a, c],
        [c, a],
      ] as [string, string][]) {
        if (m1 !== A && m2 !== A) continue;
        const B = m1 === A ? m2 : m1;
        if (B === C) continue;
        if (related(gs, 'par', line2(m.p, p.p), line2(B, C)))
          return 'Réciproque du théorème des milieux';
      }
    }
    return null;
  }
  if (p.k === 'para') {
    const [A, B, C, D] = pts(p.v);
    if (
      related(gs, 'par', line2(A!, B!), line2(C!, D!)) &&
      related(gs, 'par', line2(A!, D!), line2(B!, C!))
    )
      return 'Un quadrilatère aux côtés opposés parallèles est un parallélogramme';
    // diagonales de même milieu
    const mids = facts(gs, 'mid') as { p: string; s: string }[];
    const d1 = seg2(A!, C!);
    const d2 = seg2(B!, D!);
    if (mids.some((m) => m.s === d1 && mids.some((n) => n.s === d2 && n.p === m.p)))
      return 'Un quadrilatère dont les diagonales ont le même milieu est un parallélogramme';
    return null;
  }
  return null;
}

// ── juge ───────────────────────────────────────────────────────────────────

export interface GeoVerdict {
  kind:
    | 'empty'
    | 'syntax'
    | 'trap'
    | 'open'
    | 'pending'
    | 'step'
    | 'false'
    | 'unjustified'
    | 'useless';
  atoms: string[];
  title?: string;
  message?: string;
  badChip?: number;
  complete?: boolean;
  line?: Line;
  hora?: number;
  final?: boolean;
  rule?: { id: string; card: string; label: string; hora: number; why: string };
  trap?: { line: Line; title: string; why: string };
}

export function propLine(p: Prop): Line {
  const n: Node = { k: 's', n: propText(p) };
  return { dis: [{ sides: [n] }] };
}
function lineProp(L: Line): Prop {
  return parseProp(L.dis[0]!.sides.map((s) => s.n ?? '').join(' '));
}

/** une proposition est-elle complète (encore des bouts à poser ?) */
function looksOpen(text: string): boolean {
  return /(\/\/|⊥|milieu de|∈|rectangle en)\s*$/.test(text) || /^\S+$/.test(text.trim());
}

export function geoJudge(
  gs: GeoState,
  traps: { line: string; title: string; why: string }[],
  chips: Chip[],
  opts?: { verify?: boolean },
): GeoVerdict {
  const A = chips.flatMap((c) => c.atoms);
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  const text = A.join(' ').replace(/\s+/g, ' ').trim();
  let p: Prop;
  try {
    p = parseProp(text);
  } catch (e) {
    if (looksOpen(text)) return { kind: 'open', atoms: A, complete: false };
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Ça commence mal',
      message: (e as Error).message,
      badChip: chips.length - 1,
    };
  }
  const line = propLine(p);
  const key = propKey(p);
  const trap = traps.find((t) => {
    try {
      return propKey(parseProp(t.line)) === key;
    } catch {
      return false;
    }
  });
  if (trap)
    return {
      kind: 'trap',
      atoms: A,
      title: 'Piège : ' + trap.title.charAt(0).toLowerCase() + trap.title.slice(1),
      message: trap.why,
      trap: { line, title: trap.title, why: trap.why },
      badChip: -1,
    };
  if (gs.facts.has(key))
    return {
      kind: 'useless',
      atoms: A,
      line,
      title: 'Tu le sais déjà',
      message: 'C’est déjà dans l’énoncé ou dans ta feuille.',
    };
  const why = justify(gs, p);
  if (!why) {
    if (!opts?.verify) return { kind: 'pending', atoms: A, complete: true };
    return {
      kind: 'unjustified',
      atoms: A,
      title: 'D’où vient-elle ?',
      message:
        'Cette propriété ne découle pas encore de ce que tu as écrit. Cherche une propriété du cours dont TOUTES les conditions sont dans l’énoncé ou dans ta feuille.',
    };
  }
  const final = propKey(gs.goal) === key;
  return {
    kind: 'step',
    atoms: A,
    line,
    final,
    hora: 2,
    rule: {
      id: 'geometrie',
      card: '',
      label: why,
      hora: 2,
      why: final ? 'C’est ce qu’il fallait démontrer.' : why + '.',
    },
  };
}

export function addGeoLine(gs: GeoState, L: Line): void {
  const p = lineProp(L);
  gs.facts.set(propKey(p), p);
}
export function geoGoalReached(gs: GeoState): boolean {
  return gs.facts.has(propKey(gs.goal));
}
export function geoEstablished(gs: GeoState, L: Line): boolean {
  try {
    return gs.facts.has(propKey(lineProp(L)));
  } catch {
    return false;
  }
}
/** le texte (choix du QCM) est-il la proposition à démontrer ? */
export function geoIsGoal(gs: GeoState, text: string): boolean {
  try {
    return propKey(parseProp(text)) === propKey(gs.goal);
  } catch {
    return false;
  }
}
export function geoPathLines(spec: GeometrySpec): Line[] {
  return spec.path.map((t) => propLine(parseProp(t)));
}

/** les bouts d'une proposition (pour la palette) : droites, segments, points, relations */
export function geoChipsOf(text: string): string[] {
  try {
    const p = parseProp(text);
    switch (p.k) {
      case 'par':
        return [p.a, '//', p.b];
      case 'perp':
        return [p.a, '⊥', p.b];
      case 'mid':
        return [p.p, 'milieu de', p.s];
      case 'on':
        return [p.p, '∈', p.s];
      case 'para':
        return [p.v, 'parallélogramme'];
      case 'rect':
        return [p.t, 'rectangle en', p.at];
    }
  } catch {
    return [];
  }
}
export const GEO_RELATIONS = ['//', '⊥', 'milieu de', '∈', 'parallélogramme', 'rectangle en'];
